import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { useQueries, useQuery } from '@tanstack/react-query'
import BGTABI from 'abis/BGT.json'
import ERC20ABI from 'abis/erc20.json'
import { RefetchPolicy, TOKENS_BALANCES_POLICY } from 'config/RefetchPolicies'
import { BGT_ADDRESSES, MULTICALL_3_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import type { Abi } from 'viem'
import { Address, isAddress } from 'viem'
import { useConfig } from 'wagmi'
import { readContract } from 'wagmi/actions'

import MULTICALL3_ABI from '../../abis/multicall3.json'

export const CURRENCY_BALANCE_QUERY_KEY = 'currencyBalance'

/**
 * Generates the specific React Query options for a single currency balance.
 * This ensures both the single hook and the list hook use the EXACT same query Key and Fn.
 */
export function getCurrencyBalanceQueryOptions(
  config: any,
  chainId: number | undefined,
  account: string | undefined,
  currency: Currency | undefined,
  refetchPolicy: RefetchPolicy
) {
  const isToken = currency?.isToken ?? false
  const isNative = currency?.isNative ?? false
  const tokenAddress = isToken ? currency?.wrapped.address : undefined
  const enabled = !!chainId && !!account && !!currency

  // Normalized Cache Key: ['currencyBalance', chainId, account, address | 'NATIVE']
  const queryKey = [CURRENCY_BALANCE_QUERY_KEY, chainId, account, isToken ? tokenAddress : 'NATIVE'] as const

  return {
    ...refetchPolicy,
    queryKey,
    enabled,
    queryFn: async () => {
      if (!enabled) return null

      // 1. Native Balance Fetch
      if (isNative) {
        const multicallAddress = MULTICALL_3_ADDRESSES[chainId] as Address
        if (!multicallAddress) throw new Error('Multicall address missing')

        const balance = await readContract(config, {
          address: multicallAddress,
          abi: MULTICALL3_ABI as Abi,
          functionName: 'getEthBalance',
          args: [account as Address],
          chainId,
        })
        return balance as bigint
      }

      // 2. Token Balance Fetch
      if (isToken && tokenAddress) {
        // Special BGT Logic
        const bgtAddress = BGT_ADDRESSES[chainId]?.toLowerCase()
        const isBgt = bgtAddress && tokenAddress.toLowerCase() === bgtAddress

        const balance = await readContract(config, {
          address: tokenAddress as Address,
          abi: (isBgt ? BGTABI : ERC20ABI) as Abi,
          functionName: isBgt ? 'unboostedBalanceOf' : 'balanceOf',
          args: [account as Address],
          chainId,
        })
        return balance as bigint
      }

      return null
    },
  }
}

// --- Hook Implementations ---

export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[],
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): [{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }, boolean] {
  const { chainId } = useConnection()
  const config = useConfig()

  const validatedTokens = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address ?? '') !== false) ?? [],
    [tokens]
  )

  // Generate options for every token in the list
  const queriesOptions = useMemo(
    () =>
      validatedTokens.map((token) => getCurrencyBalanceQueryOptions(config, chainId, address, token, refetchPolicy)),
    [validatedTokens, config, chainId, address, refetchPolicy]
  )

  // Execute Dynamic Parallel Queries
  // Viem Client will automatically batch these into a single Multicall if configured
  const results = useQueries({ queries: queriesOptions })

  const anyLoading = results.some((r) => r.isLoading)

  const balanceMap = useMemo(() => {
    return validatedTokens.reduce<{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }>((memo, token, i) => {
      const result = results[i]
      if (result.status === 'success' && result.data !== undefined && result.data !== null) {
        const value = result.data as bigint
        const amount = JSBI.BigInt(value.toString())
        memo[token.address] = CurrencyAmount.fromRawAmount(token, amount)
      }
      return memo
    }, {})
  }, [validatedTokens, results])

  return [balanceMap, anyLoading]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[],
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): { [tokenAddress: string]: CurrencyAmount<Token> | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens, refetchPolicy)[0]
}

export function useTokenBalance(
  account?: string,
  token?: Token,
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): CurrencyAmount<Token> | undefined {
  const { chainId } = useConnection()
  const config = useConfig()

  const options = getCurrencyBalanceQueryOptions(config, chainId, account, token, refetchPolicy)
  const { data } = useQuery(options)

  return useMemo(() => {
    if (!token || data === undefined || data === null) return undefined
    return CurrencyAmount.fromRawAmount(token, JSBI.BigInt(data.toString()))
  }, [token, data])
}

export function useNativeBalance(
  account?: string,
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): bigint | undefined {
  const { chainId } = useConnection()
  const config = useConfig()

  // Dummy Native Currency object to trigger the native logic
  const nativeCurrency = useMemo(() => ({ isNative: true, isToken: false } as unknown as Currency), [])

  const options = getCurrencyBalanceQueryOptions(config, chainId, account, nativeCurrency, refetchPolicy)
  const { data } = useQuery(options)

  return data as bigint | undefined
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[],
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): (CurrencyAmount<Currency> | undefined)[] {
  const { chainId } = useConnection()
  const config = useConfig()

  // Generate options for mixed list (Native + Tokens)
  const queriesOptions = useMemo(
    () =>
      (currencies ?? []).map((currency) =>
        currency
          ? getCurrencyBalanceQueryOptions(config, chainId, account, currency, refetchPolicy)
          : {
              ...refetchPolicy,
              enabled: false,
              queryKey: [CURRENCY_BALANCE_QUERY_KEY, chainId, account, 'NULL'] as const,
            }
      ),
    [currencies, config, chainId, account, refetchPolicy]
  )

  const results = useQueries({ queries: queriesOptions })

  return useMemo(
    () =>
      (currencies ?? []).map((currency, i) => {
        const result = results[i]
        if (!currency || !result || result.data === undefined || result.data === null) return undefined

        return CurrencyAmount.fromRawAmount(currency, JSBI.BigInt(result.data.toString()))
      }),
    [currencies, results]
  )
}

export default function useCurrencyBalance(
  account?: string,
  currency?: Currency,
  refetchPolicy: RefetchPolicy = TOKENS_BALANCES_POLICY
): CurrencyAmount<Currency> | undefined {
  const { chainId } = useConnection()
  const config = useConfig()

  const options = getCurrencyBalanceQueryOptions(config, chainId, account, currency, refetchPolicy)
  const { data } = useQuery(options)

  return useMemo(() => {
    if (!currency || data === undefined || data === null) return undefined
    return CurrencyAmount.fromRawAmount(currency, JSBI.BigInt(data.toString()))
  }, [currency, data])
}
